/* OpenAI Ads storefront events. Checkout events live in docs/openai-ads-shopify-pixel.js. */
(() => {
  const script = document.currentScript;
  const pixelId = script?.dataset.pixelId?.trim();
  if (!pixelId || !/^[A-Za-z0-9_-]{8,128}$/.test(pixelId) ||
      script.dataset.designMode === 'true' || window.LegalOpenAIAds) return;

  const currency = script.dataset.currency?.trim();
  let validCurrency = /^[A-Z]{3}$/.test(currency || '');
  let zeroDecimalCurrency = false;
  if (validCurrency) {
    try {
      zeroDecimalCurrency = new Intl.NumberFormat('en', { style: 'currency', currency })
        .resolvedOptions().maximumFractionDigits === 0;
    } catch (_) { validCurrency = false; }
  }
  let initialized = false;
  let initialEventsSent = false;
  let leadSent = false;
  let cartWriteQueue = Promise.resolve();
  const cartConsentMarker = 'legal_openai_ads_cart_consent';

  const consentAllowed = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return privacy?.analyticsProcessingAllowed?.() === true &&
        privacy?.marketingAllowed?.() === true &&
        privacy?.saleOfDataAllowed?.() === true;
    } catch (_) {
      return false;
    }
  };

  const eventId = () => window.crypto?.randomUUID?.() ||
    `legal-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const clickReference = () => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('oppref');
      if (fromUrl && fromUrl.length <= 1024) return fromUrl;
      const cookie = document.cookie.split('; ').find((part) => part.startsWith('__oppref='));
      const stored = cookie ? decodeURIComponent(cookie.slice('__oppref='.length)) : '';
      return stored.length <= 1024 ? stored : '';
    } catch (_) {
      return '';
    }
  };

  // Private cart attributes follow the cart into Shopify's order.customAttributes.
  // Shopify Flow uses the consent attribute before sending any order to OpenAI.
  const syncCartAttribution = (allowed) => {
    let wasSynced = initialized;
    try { wasSynced ||= window.localStorage.getItem(cartConsentMarker) === '1'; }
    catch (_) { /* Storage can be unavailable. */ }
    if (!allowed && !wasSynced) return;
    if (typeof window.fetch !== 'function') return;
    const root = window.Shopify?.routes?.root || '/';
    const attributes = {
      __openai_ads_consent: allowed ? 'yes' : '',
      __openai_ads_oppref: allowed ? clickReference() : '',
    };
    cartWriteQueue = cartWriteQueue.then(() => window.fetch(`${root}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes }),
    })).then((response) => {
      if (!response.ok) return;
      try {
        if (allowed) window.localStorage.setItem(cartConsentMarker, '1');
        else window.localStorage.removeItem(cartConsentMarker);
      } catch (_) { /* Storage is only a hint for clearing a later withdrawal. */ }
    }).catch(() => { /* A failed cart update must not block storefront events. */ });
  };

  // Shopify Liquid and Ajax append two decimal places even for JPY/KRW.
  const shopifyPriceToMinor = (value) => {
    const price = Number(value);
    if (!Number.isSafeInteger(price) || price < 0) return null;
    const minor = zeroDecimalCurrency ? price / 100 : price;
    return Number.isSafeInteger(minor) ? minor : null;
  };

  const measure = (name, data) => {
    if (!initialized || !consentAllowed()) return;
    window.oaiq('measure', name, data, { event_id: eventId() });
  };

  const sendInitialEvents = () => {
    if (initialEventsSent) return;
    initialEventsSent = true;
    measure('page_viewed', { type: 'contents' });

    const { contentId, contentName, contentType, contentPrice } = script.dataset;
    if (!contentId || !contentType) return;
    const content = { id: contentId, content_type: contentType };
    if (contentName) content.name = contentName.slice(0, 200);
    const price = shopifyPriceToMinor(contentPrice);
    if (contentType === 'product' && validCurrency && contentPrice !== '' && price !== null) {
      content.amount = price;
      content.currency = currency;
    }
    measure('contents_viewed', { type: 'contents', contents: [content] });
  };

  const sendLeadIfPresent = () => {
    if (leadSent || !document.querySelector('[data-openai-ads-lead]')) return;
    if (!initialized || !consentAllowed()) return;
    leadSent = true;
    measure('lead_created', { type: 'customer_action' });
  };

  const loadPixel = () => {
    if (window.oaiq) return;
    const queue = function () { queue.q.push(arguments); };
    queue.q = [];
    window.oaiq = queue;
    const sdk = document.createElement('script');
    sdk.async = true;
    sdk.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
    sdk.onload = () => syncCartAttribution(consentAllowed());
    document.head.appendChild(sdk);
  };

  const syncConsent = () => {
    const allowed = consentAllowed();
    if (allowed && !initialized) {
      loadPixel();
      window.oaiq('consent', false);
      window.oaiq('init', { pixelId });
      window.oaiq('consent', true);
      initialized = true;
      sendInitialEvents();
      sendLeadIfPresent();
    } else if (initialized) {
      window.oaiq('consent', allowed);
    }
    syncCartAttribution(allowed);
  };

  const itemsAdded = (rawItems, requestedQuantities) => {
    if (!initialized || !consentAllowed()) return;
    const items = Array.isArray(rawItems) ? rawItems : [rawItems];
    const requestedById = new Map();
    if (Array.isArray(requestedQuantities)) {
      requestedQuantities.forEach((item) => {
        const id = item?.variant_id || item?.id;
        const quantity = Number(item?.quantity);
        if (id && Number.isSafeInteger(quantity) && quantity > 0) {
          requestedById.set(String(id), (requestedById.get(String(id)) || 0) + quantity);
        }
      });
    }
    const contents = [];
    let total = 0;
    let allPricesKnown = validCurrency;

    items.forEach((item) => {
      if (!item) return;
      const id = item.variant_id || item.id;
      const requestedQuantity = Array.isArray(requestedQuantities)
        ? requestedById.get(String(id))
        : requestedQuantities;
      const quantity = Number(requestedQuantity || item.quantity);
      if (!id || !Number.isSafeInteger(quantity) || quantity < 1) return;
      const content = { id: String(id), content_type: 'product', quantity };
      const name = item.product_title || item.title;
      if (typeof name === 'string') content.name = name.slice(0, 200);
      const price = shopifyPriceToMinor(item.final_price ?? item.price);
      if ((item.final_price == null && item.price == null) || price === null) {
        allPricesKnown = false;
      } else if (validCurrency) {
        content.amount = price;
        content.currency = currency;
        total += price * quantity;
      }
      contents.push(content);
    });

    if (!contents.length) return;
    const data = { type: 'contents', contents };
    if (allPricesKnown && Number.isSafeInteger(total)) {
      data.amount = total;
      data.currency = currency;
    }
    measure('items_added', data);
    syncCartAttribution(true);
  };

  window.LegalOpenAIAds = { itemsAdded };
  document.addEventListener('visitorConsentCollected', syncConsent);
  document.addEventListener('DOMContentLoaded', sendLeadIfPresent);

  if (window.Shopify?.customerPrivacy) syncConsent();
  else if (typeof window.Shopify?.loadFeatures === 'function') {
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }],
        () => syncConsent());
    } catch (_) { /* No advertising event is sent without Shopify consent. */ }
  }
  window.addEventListener('load', syncConsent, { once: true });
})();
